/**
 * 労働区分（通常／別枠／労働外）の設定タブ。マスタ管理 → 勤怠設定 → 労働区分。
 *
 * 会社ごとに「どの区分があるか」と「その区分をどう扱うか」（労働時間に含める／上限計算に含める／
 * 上限超えを許容（64条）／休息として扱う／事由必須／陸上へ通知）を決める。
 * 併せて「食事の扱い」（毎回記録するか、1日あたり一定分を自動控除するか）を会社既定＋船別で設定する。
 *
 * ガードレール（サーバー側でも弾く）: 通常業務・臨時作業は労働時間から外せない。
 * 2023年4月の船員法改正で、訓練（操練）や当直交代の引継ぎは労働時間に含めることになったため。
 */
import React, { useEffect, useState } from 'react';
import * as api from '../services/api';

export interface LaborCategory {
  id: string; code: string; name: string; color: string;
  counts_as_work: number; counts_toward_limit: number; permits_over_limit: number; is_premium: number;
  requires_reason: number; counts_as_rest: number; notify_shore: number;
  legal_basis?: string | null; description?: string | null;
  display_order: number; is_active: number; is_system: number;
}
interface MealRule { id: string; vessel_id: string; vessel_name?: string | null; mode: 'record' | 'auto' | 'off'; auto_minutes: number }
interface VesselItem { id: string; vessel_name: string; is_active?: number }

/** 区分のバッジ（業務・作業マスタの一覧などで使う） */
export function LaborCategoryBadge({ code, cats }: { code: string; cats: LaborCategory[] }) {
  const c = cats.find(x => x.code === code);
  const name = c?.name ?? (code === 'normal' ? '通常業務' : code === 'special' ? '臨時作業' : code);
  const color = c?.color ?? '#0057d9';
  return (
    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, color, background: color + '14', border: `1px solid ${color}55`, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>
      {name}
    </span>
  );
}

/** 区分の扱いを短い言葉にする（3本のどれに入るか） */
export function bucketLabel(c: Pick<LaborCategory, 'counts_as_work' | 'counts_toward_limit' | 'permits_over_limit'>): { label: string; color: string } {
  if (!c.counts_as_work) return { label: '労働外', color: '#64748b' };
  if (!c.counts_toward_limit || c.permits_over_limit) return { label: '別枠', color: '#f59e0b' };
  return { label: '通常', color: '#0057d9' };
}

const FLAGS: Array<{ key: keyof LaborCategory; label: string; help: string }> = [
  { key: 'counts_as_work',      label: '労働時間に含める',   help: 'OFF＝労働時間0として扱う（食事・私的作業など）' },
  { key: 'counts_toward_limit', label: '上限計算に含める',   help: 'OFF＝1日14時間・週72時間などの上限計算から除外（非常作業68条）' },
  { key: 'permits_over_limit',  label: '上限超えを許容',     help: 'ON＝上限を超えても違反にしない（安全臨時労働64条）' },
  { key: 'is_premium',          label: '割増手当の対象',     help: '給与計算の目印。集計値には影響しない' },
  { key: 'counts_as_rest',      label: '休息として扱う',     help: 'ON＝休息時間の計算で休息に数える（食事など）' },
  { key: 'requires_reason',     label: '事由を必須にする',   help: 'ON＝船員アプリで事由を入れないと記録できない' },
  { key: 'notify_shore',        label: '記録時に陸上へ通知', help: 'ON＝記録されると陸上管理者へ連絡＋プッシュ通知（承認は不要）' },
];

const box: React.CSSProperties = { background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 20, marginBottom: 20 };
const btn: React.CSSProperties = { padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' };
const primary: React.CSSProperties = { ...btn, background: '#0057d9', color: '#fff', border: 'none' };

export default function LaborCategoriesTab() {
  const [cats, setCats] = useState<LaborCategory[]>([]);
  const [rules, setRules] = useState<MealRule[]>([]);
  const [vessels, setVessels] = useState<VesselItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState<string | null>(null);
  const [edit, setEdit] = useState<Record<string, Partial<LaborCategory>>>({});
  const [newName, setNewName] = useState('');
  // 食事の扱い（会社既定 + 船別）
  const [mealMode, setMealMode] = useState<'record' | 'auto' | 'off'>('record');
  const [mealMin, setMealMin] = useState('90');
  const [vesselOverride, setVesselOverride] = useState<{ vessel_id: string; mode: 'record' | 'auto' | 'off'; auto_minutes: string }>({ vessel_id: '', mode: 'auto', auto_minutes: '90' });
  const [mealSaved, setMealSaved] = useState(false);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [c, r, v] = await Promise.all([
        api.get<any>('/labor-categories', { all: '1' }),
        api.get<any>('/labor-categories/meal-rules'),
        api.get<any>('/vessels'),
      ]);
      setCats(c?.data ?? []);
      const rows: MealRule[] = r?.data ?? [];
      setRules(rows);
      const dflt = rows.find(x => x.vessel_id === '');
      if (dflt) { setMealMode(dflt.mode); setMealMin(String(dflt.auto_minutes || 90)); }
      setVessels(((v?.data ?? v?.vessels ?? []) as VesselItem[]).filter(x => x.is_active === undefined || x.is_active === 1));
    } catch (e: any) { setError(e?.message ?? '読み込みに失敗しました'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const val = <K extends keyof LaborCategory>(c: LaborCategory, k: K): LaborCategory[K] => (edit[c.id]?.[k] as LaborCategory[K] | undefined) ?? c[k];
  const setVal = (c: LaborCategory, k: keyof LaborCategory, v: unknown) => setEdit(p => ({ ...p, [c.id]: { ...(p[c.id] ?? {}), [k]: v } }));
  const dirty = (c: LaborCategory) => !!edit[c.id] && Object.keys(edit[c.id]!).length > 0;

  const save = async (c: LaborCategory) => {
    setError('');
    try {
      await api.put(`/labor-categories/${c.id}`, edit[c.id] ?? {});
      setEdit(p => { const n = { ...p }; delete n[c.id]; return n; });
      setSavedId(c.id); setTimeout(() => setSavedId(null), 2000);
      await load();
    } catch (e: any) { setError(e?.message ?? '保存に失敗しました'); }
  };
  const toggleActive = async (c: LaborCategory) => {
    setError('');
    try { await api.put(`/labor-categories/${c.id}`, { is_active: c.is_active ? 0 : 1 }); await load(); }
    catch (e: any) { setError(e?.message ?? '更新に失敗しました'); }
  };
  const addCustom = async () => {
    if (!newName.trim()) return;
    setError('');
    try { await api.post('/labor-categories', { name: newName.trim(), color: '#6366f1', counts_as_work: 0, counts_toward_limit: 0, counts_as_rest: 1 }); setNewName(''); await load(); }
    catch (e: any) { setError(e?.message ?? '追加に失敗しました'); }
  };
  const remove = async (c: LaborCategory) => {
    if (!confirm(`区分「${c.name}」を削除しますか？`)) return;
    setError('');
    try { await api.del(`/labor-categories/${c.id}`); await load(); }
    catch (e: any) { setError(e?.message ?? '削除に失敗しました'); }
  };

  const saveMealDefault = async () => {
    setError('');
    try {
      await api.put('/labor-categories/meal-rules', { vessel_id: '', mode: mealMode, auto_minutes: Number(mealMin) || 0 });
      setMealSaved(true); setTimeout(() => setMealSaved(false), 2000);
      await load();
    } catch (e: any) { setError(e?.message ?? '保存に失敗しました'); }
  };
  const saveVesselOverride = async () => {
    if (!vesselOverride.vessel_id) { setError('船を選んでください'); return; }
    setError('');
    try {
      await api.put('/labor-categories/meal-rules', { vessel_id: vesselOverride.vessel_id, mode: vesselOverride.mode, auto_minutes: Number(vesselOverride.auto_minutes) || 0 });
      await load();
    } catch (e: any) { setError(e?.message ?? '保存に失敗しました'); }
  };
  const removeOverride = async (vid: string) => {
    try { await api.del(`/labor-categories/meal-rules/${vid}`); await load(); } catch (e: any) { setError(e?.message ?? '削除に失敗しました'); }
  };

  const modeLabel = (m: string, min: number) => m === 'auto' ? `自動控除（1日 ${min} 分）` : m === 'off' ? '扱わない' : '毎回記録（船員が「食事・共食」を記録）';

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>労働区分（通常／別枠／労働外）</div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4, lineHeight: 1.7 }}>
          作業の記録に付く区分と、その扱いを会社ごとに決めます。労働時間は
          <b style={{ color: '#0057d9' }}> 通常</b>（上限計算の対象）／
          <b style={{ color: '#f59e0b' }}> 別枠</b>（非常作業68条＝上限計算から除外、安全臨時労働64条＝上限超えが合法・割増）／
          <b style={{ color: '#64748b' }}> 労働外</b>（食事・自発的作業・私的作業など＝労働時間に含めない）の3本で集計されます。
          区分は「業務・作業マスタ」で作業種別に割り当てます。
        </div>
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{error}</div>}

      {/* 区分一覧 */}
      <div style={{ ...box, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              {['区分', '3本の位置づけ', '扱い', '法的根拠・説明', '状態', '操作'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cats.map(c => {
              const cur = { ...c, ...(edit[c.id] ?? {}) } as LaborCategory;
              const b = bucketLabel(cur);
              const locked = c.code === 'normal' || c.code === 'special';
              return (
                <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: c.is_active ? 1 : 0.55, verticalAlign: 'top' }}>
                  <td style={{ padding: '12px 14px', minWidth: 170 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="color" value={String(val(c, 'color'))} onChange={e => setVal(c, 'color', e.target.value)} style={{ width: 24, height: 24, border: 'none', padding: 0, background: 'none', cursor: 'pointer' }} title="色" />
                      <input value={String(val(c, 'name'))} onChange={e => setVal(c, 'name', e.target.value)}
                        style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 8px', fontSize: 13, fontWeight: 700, width: 130 }} />
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>code: {c.code}{c.is_system ? '（既定）' : ''}</div>
                  </td>
                  <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: b.color, background: b.color + '14', border: `1px solid ${b.color}55`, borderRadius: 999, padding: '3px 10px' }}>{b.label}</span>
                  </td>
                  <td style={{ padding: '12px 14px', minWidth: 300 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                      {FLAGS.map(f => {
                        const disabled = locked && f.key === 'counts_as_work';
                        return (
                          <label key={f.key} title={f.help} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: disabled ? '#94a3b8' : '#374151', cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
                            <input type="checkbox" checked={!!val(c, f.key)} disabled={disabled} onChange={e => setVal(c, f.key, e.target.checked ? 1 : 0)} style={{ accentColor: '#0057d9' }} />
                            {f.label}
                          </label>
                        );
                      })}
                    </div>
                    {locked && <div style={{ fontSize: 11, color: '#b45309', marginTop: 6 }}>訓練・引継ぎを含むため、労働時間からは外せません（2023年4月改正）</div>}
                  </td>
                  <td style={{ padding: '12px 14px', minWidth: 240 }}>
                    <input value={String(val(c, 'legal_basis') ?? '')} onChange={e => setVal(c, 'legal_basis', e.target.value)} placeholder="法的根拠（例：船員法第68条）"
                      style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 8px', fontSize: 12, width: '100%', marginBottom: 4 }} />
                    <textarea value={String(val(c, 'description') ?? '')} onChange={e => setVal(c, 'description', e.target.value)} rows={2} placeholder="説明"
                      style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 8px', fontSize: 12, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
                  </td>
                  <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: c.is_active ? '#f0fdf4' : '#f8fafc', color: c.is_active ? '#059669' : '#94a3b8', border: `1px solid ${c.is_active ? '#bbf7d0' : '#e2e8f0'}` }}>
                      {c.is_active ? '有効' : '無効'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <button onClick={() => save(c)} disabled={!dirty(c)} style={{ ...primary, opacity: dirty(c) ? 1 : 0.4, cursor: dirty(c) ? 'pointer' : 'default' }}>
                        {savedId === c.id ? '保存しました' : '保存'}
                      </button>
                      {c.code !== 'normal' && <button onClick={() => toggleActive(c)} style={btn}>{c.is_active ? '無効にする' : '有効にする'}</button>}
                      {!c.is_system && <button onClick={() => remove(c)} style={{ ...btn, color: '#dc2626', borderColor: '#fecaca' }}>削除</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 14, borderTop: '1px solid #f1f5f9', background: '#fafbfc' }}>
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="会社独自の区分を追加（例：待機時間）"
            style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13, width: 320 }} />
          <button onClick={addCustom} style={primary}>追加</button>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>追加した区分は「労働外・休息として扱う」で作られます。扱いは上の表で変えてください。</span>
        </div>
      </div>

      {/* 食事の扱い */}
      <div style={box}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>食事の扱い</div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, lineHeight: 1.7 }}>
          労働から解放されている食事の時間は労働時間に含めません。船員に毎回「食事・共食」を記録させるか、
          勤務があった日に一定の分数を自動で控除するかを選べます。船ごとに変えることもできます。
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#374151', width: 90 }}>会社の既定</span>
          <select value={mealMode} onChange={e => setMealMode(e.target.value as 'record' | 'auto' | 'off')}
            style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13 }}>
            <option value="record">毎回記録（船員が「食事・共食」を記録する）</option>
            <option value="auto">自動控除（勤務があった日に一定分を差し引く）</option>
            <option value="off">扱わない（従来どおり）</option>
          </select>
          {mealMode === 'auto' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              1日あたり <input type="number" min={0} max={600} value={mealMin} onChange={e => setMealMin(e.target.value)} style={{ width: 70, border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13 }} /> 分
              <span style={{ fontSize: 12, color: '#94a3b8' }}>（例: 30分×3食＝90分）</span>
            </label>
          )}
          <button onClick={saveMealDefault} style={primary}>{mealSaved ? '保存しました' : '既定を保存'}</button>
        </div>
        <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, marginTop: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>船ごとの上書き</div>
          {rules.filter(r => r.vessel_id !== '').length === 0 && <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>上書きはありません（全船で会社の既定が効いています）</div>}
          {rules.filter(r => r.vessel_id !== '').map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, padding: '6px 0' }}>
              <span style={{ fontWeight: 700, minWidth: 140 }}>{r.vessel_name ?? r.vessel_id}</span>
              <span style={{ color: '#374151' }}>{modeLabel(r.mode, r.auto_minutes)}</span>
              <button onClick={() => removeOverride(r.vessel_id)} style={{ ...btn, fontSize: 12 }}>既定に戻す</button>
            </div>
          ))}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
            <select value={vesselOverride.vessel_id} onChange={e => setVesselOverride(p => ({ ...p, vessel_id: e.target.value }))}
              style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13 }}>
              <option value="">船を選ぶ</option>
              {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
            </select>
            <select value={vesselOverride.mode} onChange={e => setVesselOverride(p => ({ ...p, mode: e.target.value as 'record' | 'auto' | 'off' }))}
              style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13 }}>
              <option value="record">毎回記録</option>
              <option value="auto">自動控除</option>
              <option value="off">扱わない</option>
            </select>
            {vesselOverride.mode === 'auto' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                1日 <input type="number" min={0} max={600} value={vesselOverride.auto_minutes} onChange={e => setVesselOverride(p => ({ ...p, auto_minutes: e.target.value }))} style={{ width: 70, border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13 }} /> 分
              </label>
            )}
            <button onClick={saveVesselOverride} style={btn}>この船に設定</button>
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.7 }}>
        ※ 非常作業（68条）は賃金上の労働時間に含め、上限計算からだけ除外します。安全臨時労働（64条）は労働時間・上限計算に含めますが、上限超えを違反にせず割増の対象とします。
        条文の扱いは運輸局・社労士の確認を経て、法的根拠の欄で文言を調整してください。
      </div>
    </div>
  );
}
